import { loginUser } from "@/lib/api/login"
import { clearPermissionsCache } from "@/hooks/permissions"
import {
  AuthTokens,
  LoginCredentials,
} from "@/definitions/types/api/auth"
import {
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom"
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
  useRef,
} from "react"

import {
  AUTH_LOCAL_STORAGE_KEY,
  AUTH_SEARCH_PARAMS_KEY,
  FRONTEND_ROUTES,
  DELAY,
} from "@/constants"
import { verifyToken } from "@/service/token/verify-token"
import LoadingSkeleton from "@/components/shared/LoadingSkeleton"
import { logoutService } from "@/service/logout/logout"
import { toast } from "@/hooks/use-toast"
import { useDomain } from "@/hooks/useDomain"

const VERIFICATION_CACHE_KEY = "token_verification_cache"
const ONE_HOUR_MS = 1 * 60 * 60 * 1000

type AuthContextType = AuthTokens & {
  login: (credentials: LoginCredentials) => Promise<void>
  logout: () => void
}

type LocalStorageAuthData = Omit<
  AuthTokens,
  "isLoading" | "isAuthenticated"
>

const AuthContext = createContext<
  AuthContextType | undefined
>(undefined)

const getVerificationCache = () => {
  const cache = localStorage.getItem(VERIFICATION_CACHE_KEY)
  if (!cache) return null

  try {
    const { timestamp, isValid } = JSON.parse(cache)
    if (Date.now() - timestamp > ONE_HOUR_MS) {
      localStorage.removeItem(VERIFICATION_CACHE_KEY)
      return null
    }
    return isValid
  } catch (error) {
    console.error(
      "Error parsing verification cache:",
      error
    )
    localStorage.removeItem(VERIFICATION_CACHE_KEY)
    return null
  }
}

const getStoredAuthDataFromLocalStorage =
  (): LocalStorageAuthData | null => {
    const storedAuthData = localStorage.getItem(
      AUTH_LOCAL_STORAGE_KEY
    )
    if (!storedAuthData) return null

    try {
      return JSON.parse(storedAuthData)
    } catch (error) {
      console.error(
        "Error parsing stored auth data:",
        error
      )
      localStorage.removeItem(AUTH_LOCAL_STORAGE_KEY)
      return null
    }
  }

const removeAuthDataFromLocalStorage = () => {
  try {
    localStorage.removeItem(AUTH_LOCAL_STORAGE_KEY)
    localStorage.removeItem(VERIFICATION_CACHE_KEY)
    clearPermissionsCache()
  } catch (error) {
    console.error(
      "Error removing auth data from localStorage:",
      error
    )
  }
}

const addVerificationCache = (isValid: boolean) => {
  try {
    localStorage.setItem(
      VERIFICATION_CACHE_KEY,
      JSON.stringify({
        timestamp: Date.now(),
        isValid,
      })
    )
  } catch (error) {
    console.error("Error saving verification cache:", error)
  }
}

export function AuthProvider({
  children,
}: {
  children: ReactNode
}) {
  const domain = useDomain()
  const [authData, setAuthData] = useState<AuthTokens>({
    email: null,
    fullName: null,
    accessToken: null,
    expiresIn: null,
    isAuthenticated: false,
    isLoading: true,
  })
  const navigate = useNavigate()
  const location = useLocation()
  const pathname = location.pathname
  const [searchParams] = useSearchParams()
  const isInitialized = useRef(false)

  const setTimeoutLoadingState = (
    isLoading: boolean,
    delay?: number
  ) => {
    if (delay) {
      return setTimeout(() => {
        try {
          setAuthData((prev) => ({ ...prev, isLoading }))
        } catch (error) {
          console.error(
            "Error updating loading state:",
            error
          )
        }
      }, delay)
    }
    try {
      return setAuthData((prev) => ({ ...prev, isLoading }))
    } catch (error) {
      console.error("Error updating loading state:", error)
    }
  }

  const handleAuthenticatedUser = useCallback(
    (authData: LocalStorageAuthData) => {
      const updateAuthData = () => {
        try {
          setAuthData((prev) => ({
            ...prev,
            ...authData,
            isAuthenticated: true,
            isLoading: false,
          }))
        } catch (error) {
          console.error("Error updating auth data:", error)
        }
      }

      if (pathname === FRONTEND_ROUTES.LOGIN.route) {
        navigate(FRONTEND_ROUTES.MAIN.route)
        return setTimeout(() => {
          try {
            updateAuthData()
          } catch (error) {
            console.error(
              "Error in delayed auth update:",
              error
            )
          }
        }, DELAY)
      }
      updateAuthData()
    },
    [navigate, pathname]
  )

  const handleUnauthenticatedUser = useCallback(() => {
    navigate(FRONTEND_ROUTES.LOGIN.route)
    removeAuthDataFromLocalStorage()

    setTimeoutLoadingState(true)
    setTimeoutLoadingState(false, DELAY)
  }, [navigate])

  // Initialize auth only once on mount
  useEffect(() => {
    if (isInitialized.current) return
    isInitialized.current = true

    const initializeAuth = async () => {
      const storedAuthData =
        getStoredAuthDataFromLocalStorage()
      if (!storedAuthData) {
        return handleUnauthenticatedUser()
      }

      const cachedVerification = getVerificationCache()
      if (cachedVerification !== null) {
        return cachedVerification
          ? handleAuthenticatedUser(storedAuthData)
          : handleUnauthenticatedUser()
      }

      try {
        const isValid = await verifyToken(domain)
        addVerificationCache(isValid)

        if (isValid) {
          return handleAuthenticatedUser(storedAuthData)
        }
        return handleUnauthenticatedUser()
      } catch {
        return handleUnauthenticatedUser()
      }
    }

    initializeAuth()
  }, [
    domain,
    handleAuthenticatedUser,
    handleUnauthenticatedUser,
  ])

  useEffect(() => {
    const getTokenInLoginPage = () => {
      if (pathname !== FRONTEND_ROUTES.LOGIN.route) {
        return null
      }
      const authToken = searchParams.get(
        AUTH_SEARCH_PARAMS_KEY
      )
      if (!authToken) {
        return null
      }

      try {
        const decodedToken = atob(authToken)

        const parsedTokenData = JSON.parse(decodedToken)

        setAuthData((prev) => ({
          ...prev,
          ...parsedTokenData,
          isLoading: false,
          isAuthenticated: true,
        }))

        try {
          localStorage.setItem(
            AUTH_LOCAL_STORAGE_KEY,
            decodedToken
          )
          addVerificationCache(true)
        } catch (storageError) {
          console.error(
            "Error saving token to localStorage:",
            storageError
          )
        }

        navigate(FRONTEND_ROUTES.MAIN.route)
      } catch (error) {
        console.error(
          "Error decoding or parsing token:",
          error
        )
        toast({
          title: "Erro de autenticação",
          description:
            "Token inválido ou corrompido. Redirecionando para login.",
          variant: "destructive",
        })
        handleUnauthenticatedUser()
      }
    }
    getTokenInLoginPage()
  }, [
    pathname,
    searchParams,
    navigate,
    handleUnauthenticatedUser,
  ])

  const login = async (credentials: LoginCredentials) => {
    try {
      const data = await loginUser(credentials)
      setAuthData((prev) => ({
        ...prev,
        ...data,
        isLoading: false,
        isAuthenticated: true,
      }))

      try {
        localStorage.setItem(
          AUTH_LOCAL_STORAGE_KEY,
          JSON.stringify(data)
        )
        addVerificationCache(true)
      } catch (storageError) {
        console.error(
          "Error saving auth data to localStorage:",
          storageError
        )
      }

      navigate(FRONTEND_ROUTES.MAIN.route)
    } catch (error) {
      console.error("Login error:", error)
      toast({
        title: "Erro de login",
        description:
          "Não foi possível fazer login. Tente novamente.",
        variant: "destructive",
      })
      setAuthData((prev) => ({
        ...prev,
        isLoading: false,
        isAuthenticated: false,
      }))
      throw error
    }
  }

  const logout = async () => {
    setAuthData((prev) => ({
      ...prev,
      email: null,
      fullName: null,
      accessToken: null,
      expiresIn: null,
      isAuthenticated: false,
      isLoading: false,
    }))
    try {
      const response = await logoutService(domain)

      if (!response?.logoutUrl) {
        throw new Error("url de logout não encontrada")
      }
      removeAuthDataFromLocalStorage()

      toast({
        title: "Saindo do sistema...",
      })

      window.location.href = response.logoutUrl
    } catch (error) {
      console.error("Erro ao fazer logout", error)
      toast({
        title: "Erro ao fazer logout",
        description: "url de logout não encontrada",
        variant: "destructive",
      })
      handleUnauthenticatedUser()
      navigate(FRONTEND_ROUTES.LOGIN.route)
    }
  }

  if (authData.isLoading) {
    return <LoadingSkeleton />
  }

  return (
    <AuthContext.Provider
      value={{ login, logout, ...authData }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error(
      "useAuth must be used within a AuthProvider"
    )
  }
  return context
}
